import type { MouseEvent, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import BpkButton, { BUTTON_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import { withScrimmedPortal } from '@skyscanner-internal/backpack-web/bpk-scrim-utils';
import { TCFClientBuilder } from '@skyscanner-internal/transparency-consent-framework-client';
import StringsPropVerification from '@web-platform/global-components-higher-order-components/StringsPropVerification';
import { getErrorMessage } from '@web-platform/global-components-utils/error-helpers/getErrorMessage';
import { trackEventFn as defaultTrackEventFn } from '@web-platform/global-components-utils/tracking';
import reactReplacePlaceholder from 'react-replace-placeholder';
import observer from 'saddlebag-observer';
import {
  acknowledgeTrackingSettings,
  getPreferences,
  isGdprInitialised,
  PREFERENCES,
  setInitialPreferences,
  setPreferences,
  showCookieBanner,
} from 'saddlebag-tracking-preferences';

import { cookieBannerTrackingEvent } from './userTrackingEvents';
import parseActiveVendorList from './utils/parseActiveVendorList';

import type TCFClient from '@skyscanner-internal/transparency-consent-framework-client';
import type { Logger } from '@web-platform/global-components-types/utils';
import type { TrackingEventFn } from '@web-platform/global-components-utils/tracking';
import type { Common } from 'trackolding/lib/modern';

import STYLES from './CookieBanner.module.scss';

const stringsKeys = [
  'title',
  'declineAllSectionA',
  'declineAllSectionB',
  'declineAllSectionC',
  'declineAllButtonAcceptAll',
  'declineAllButtonManageSettings',
  'declineAllButtonAcceptEssentialOnly',
] as const;

// Todo: When we're prepared to incorporate the "decline all" strings, we must combine stringsKeys and optionalStringsKeys and update the export type Strings to ensure that the "decline all" properties are also accounted for and validated. The body attribute needs to be deleted once we have removed from all its consumers.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const optionalStringsKeys = [
  'body',
  'explainYourOptions',
  'close',
  'manageOptions',
  'consentInformationSectionA',
  'consentInformationSectionB',
  'consentInformationSectionD',
] as const;

const componentName: string = 'cookie-banner';
/**
 * @interface
 * @property title - Title text.
 * @property declineAllSectionA - Text explaining how and why Skyscanner and third parties personalise the traveller's experience.
 * @property declineAllSectionB - Text explaining how and why Skyscanner and third parties use traveller's data. Also explains how users can consent and change settings or opt out or decline all.
 * @property declineAllSectionC - Text explaining how to manage consents.
 * @property declineAllButtonAcceptAll - Button text for the "Accept all" option.
 * @property declineAllButtonManageSettings - Button text for the "Manage settings" option.
 * @property declineAllButtonAcceptEssentialOnly - Button text for the "Accept essential only" option.
 *
 * @property consentInformationSectionA - (deprecated) Text explaining how and why Skyscanner and third parties personalise the traveller's experience.
 * @property consentInformationSectionB - (deprecated) Text explaining how and why Skyscanner and third parties use traveller's data. Also explains how users can consent and change settings or opt out.
 * @property consentInformationSectionC - (deprecated) Previously used to explain consent settings. Deprecated due to redesign.
 * @property consentInformationSectionD - (deprecated) Text explaining how users can withdraw consent and how to resurface the consent UI.
 * @property explainYourOptions - (deprecated) This text explains to the traveller their options.
 * @property close - (deprecated) Text to dismiss the cookie banner without modifying preferences.
 * @property manageOptions - (deprecated) Text allowing travellers to modify their privacy preferences.
 */
export type Strings = {
  [key in (typeof stringsKeys)[number]]: string;
} & {
  [key in (typeof optionalStringsKeys)[number]]?: string;
};

const saveGDPRPreferences = ({
  accepted = true,
  logger,
  tcString = '',
}: {
  accepted: boolean;
  logger: Logger;
  tcString?: string;
}): void => {
  try {
    acknowledgeTrackingSettings();
    setPreferences({
      [PREFERENCES.INFORMATION]: accepted,
      [PREFERENCES.ADVERTS]: accepted,
      ...(tcString && { [PREFERENCES.TC_STRING]: tcString }),
    });
  } catch (error) {
    logger.logWarn({
      message: `Saving GDPR Preferences failed: ${getErrorMessage(error)}`,
      level: 'warn',
      component: componentName,
    });
  }
};

type CookieBannerContentProps = {
  cookiePolicyUrl?: string;
  market: string;
  onPolicyAcceptClick: (event: MouseEvent<HTMLButtonElement> | null) => void;
  strings: Strings;
  logger: Logger;
  activeVendorList?: string;
  totalNumberOfVendors?: string | null;
  trackEventFn?: TrackingEventFn;
  dialogRef: RefObject<HTMLDivElement>;
  transparencyConsentFrameworkConfigVersionNumber?: string;
};

const mbdTrackingParams = (name: string) => {
  const cookieBannerComponentTrackingParams: Common = {
    component_name: name,
  };
  return JSON.stringify(cookieBannerComponentTrackingParams);
};

const contextCookiePolicyLink = (
  logger: Logger,
  cookiePolicyUrl: string,
  trackEventFn: TrackingEventFn,
) => ({
  tag1: {
    pair: 'tag2',
    format: (children: any) => (
      <BpkLink
        href={cookiePolicyUrl}
        key="cookiePolicyLink"
        onClick={() => {
          trackEventFn(cookieBannerTrackingEvent('Link Clicked'));
          logger.logOperationalEvent({
            eventName: 'Link Clicked',
            component: componentName,
          });
        }}
        data-tracking-element-id="cookie_banner_policy_link"
        data-tracking-common-params={mbdTrackingParams('cookie_banner_policy_link')}
        id="cookiePolicyLink"
      >
        {children}
      </BpkLink>
    ),
  },
  tag3: {
    pair: 'tag4',
    format: (children: any) => (
      <BpkLink
        href="/privacy-settings#vendors"
        key="thirdPartyVendorsLink"
        onClick={() => {
          trackEventFn(cookieBannerTrackingEvent('Vendor Link Clicked'));
          logger.logOperationalEvent({
            eventName: 'Vendor Link Clicked',
            component: componentName,
          });
        }}
        data-tracking-element-id="cookie_banner_policy_vendor_link"
        data-tracking-common-params={mbdTrackingParams('cookie_banner_policy_vendor_link')}
        id="thirdPartyVendorsLink"
      >
        {children}
      </BpkLink>
    ),
  },
  tag5: {
    pair: 'tag6',
    format: (children: any) => (
      <BpkLink
        href="/privacy-settings"
        key="privacySettingsLink"
        onClick={() => {
          trackEventFn(cookieBannerTrackingEvent('Privacy Settings Link Clicked'));
          logger.logOperationalEvent({
            eventName: 'Privacy Settings Link Clicked',
            component: componentName,
          });
        }}
        id="privacySettingsLink"
      >
        {children}
      </BpkLink>
    ),
  },
});

const CookieBannerContent = ({
  activeVendorList = '',
  cookiePolicyUrl = '/cookiepolicy.aspx',
  dialogRef,
  logger,
  market,
  onPolicyAcceptClick,
  strings,
  totalNumberOfVendors = null,
  // TODO: Remove in SRS-710
  trackEventFn = defaultTrackEventFn,
  transparencyConsentFrameworkConfigVersionNumber = '',
}: CookieBannerContentProps) => {
  const cookieHeaderRef = useRef<HTMLDivElement>(null);
  const tcfClientRef = useRef<TCFClient | null>(null);

  // TCF initialisation touches `window`, so it must not run during render — when `disableScrim` is
  // set this component renders directly rather than behind withScrimmedPortal's mount gate, which
  // means the render body also executes on the server.
  // Declared before the effects below so that `tcfClientRef` is populated by the time they run.
  useEffect(() => {
    try {
      tcfClientRef.current = TCFClientBuilder.build({
        forConsentScreen: true,
        activeVendorList: activeVendorList ? parseActiveVendorList(activeVendorList) : undefined,
        tcfConfigVersionNumber: transparencyConsentFrameworkConfigVersionNumber,
      });
    } catch (error) {
      const errorStack = error instanceof Error ? error.stack : '';
      logger.logWarn({
        message: `Unable to initialise TCF on Cookie Banner: ${errorStack}`,
        level: 'warn',
        component: componentName,
      });
      logger.logOperationalEvent({
        eventName: 'TCF: Unable to initialise on Cookie Banner',
        component: componentName,
      });
    }
  }, [activeVendorList, transparencyConsentFrameworkConfigVersionNumber, logger]);

  // Extensions which block the cookie banner from rendering will prevent the user from interacting with the page.
  // Therefore, we need to programmatically unmount the cookie banner if it is hidden from view.
  // The below useEffect will automatically invoke the decline all consents flow on the user's behalf if display:none is applied to the cookie banner.
  useEffect(() => {
    const cookieBanner = document.getElementById('cookieBannerContent');

    if (cookieBanner) {
      const { display } = window.getComputedStyle(cookieBanner);
      if (display === 'none') {
        prepareAndSaveGDPRPreferences({ accepted: false });
        trackEventFn(cookieBannerTrackingEvent('Declined'));
        logger.logOperationalEvent({ eventName: 'Declined', component: componentName });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    cookieHeaderRef.current?.focus();
  }, []);

  // Another external component could update the gdpr cookie by interacting with saddlebag-tracking-preferences directly
  // In this scenario, we want to act as if the cookie banner has been accepted
  //
  // The subscription is tracked so that if the accept button is clicked, we don't fire the onPolicyAcceptClick twice
  const [trackingPreferencesChangedSubscription, setTrackingPreferencesChangedSubscription] =
    useState(null);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO: RCN-1700
    setTrackingPreferencesChangedSubscription(
      observer.subscribe('tracking-preferences-changed', () => {
        // The gdpr cookie could change into a state where the traveller still needs to accept cookies
        // so we need to check that the new state would result in the cookie banner not being needed anymore
        if (!showCookieBanner(market)) {
          logger.logOperationalEvent({
            eventName: 'Dismissed',
            component: componentName,
          });
          onPolicyAcceptClick(null);
        }
      }),
    );
  }, [onPolicyAcceptClick, market, logger]);

  useEffect(() => {
    try {
      if (!isGdprInitialised()) {
        setInitialPreferences(market);
      }
    } catch (error) {
      logger.logWarn({
        message: `setInitialPreferences failed: ${getErrorMessage(error)}`,
        component: componentName,
      });
    }
    trackEventFn(cookieBannerTrackingEvent('Visible'));
    logger.logOperationalEvent({ eventName: 'Visible', component: componentName });
  }, [logger, market, trackEventFn]);

  function prepareAndSaveGDPRPreferences({ accepted = true }: { accepted: boolean }) {
    const tcfClient = tcfClientRef.current;

    // If TCF client is not initialised, we will save the preferences without TC String
    if (!tcfClient) {
      saveGDPRPreferences({ accepted, logger });
      return;
    }

    let tcString: string = '';

    const acceptAllOrDeclineAllVendorsMethod = accepted ? 'acceptAllVendors' : 'declineAllVendors';

    tcfClient[acceptAllOrDeclineAllVendorsMethod]()
      .then(() => {
        tcString = tcfClient.getEncodedTCString();
        tcfClient.close();
      })
      .catch((error) => {
        logger.logWarn({
          message: `Unable to ${accepted ? 'accept' : 'decline'} all vendors and persist TC String: ${error.stack}`,
          level: 'warn',
          component: componentName,
        });
        logger.logOperationalEvent({
          eventName: `TCF: ${accepted ? 'Accept' : 'Decline'} All Vendors failure`,
          component: componentName,
        });
      })
      // we will always save user's preferences, even in event of issues with TCF client failing to generate TC String
      .finally(() => {
        saveGDPRPreferences({
          accepted,
          logger,
          tcString,
        });
      });
  }

  // Due to a race condition between setting preferences and tracking events, we must ensure that event tracking occurs after preferences are set.
  // Here, we subscribe to the 'tracking-preferences-changed' event triggered from saddlebag-tracking-preferences (https://github.skyscannertools.net/web-engineering/saddlebag/blob/main/packages/saddlebag-tracking-preferences/src/cookie-store.js#L29).
  // This guarantees that tracking occurs only after preferences are set, ensuring that metrics requiring consent are properly accounted for. This is a temporary workaround until a more robust solution is implemented.
  const waitForPreferencesSaved = () =>
    new Promise<void>((resolve) => {
      observer.subscribe('tracking-preferences-changed', () => {
        if (getPreferences(market)[PREFERENCES.INFORMATION]) {
          resolve();
        }
      });
    });

  const prefChangeNewRelicAndExpTrigger = async () => {
    await waitForPreferencesSaved(); // Wait for the preferences to be saved before triggering the smart metric and New Relic event

    // This trigger events in New relic.
    logger.logOperationalEvent({ eventName: 'Accepted', component: componentName });
  };

  return (
    <div ref={dialogRef} className={STYLES['cookie-banner']} id="cookieBannerContent">
      <div
        className={STYLES['cookie-banner__wrapper']}
        data-tracking-element-id="cookie_banner"
        data-tracking-common-params={mbdTrackingParams('cookie_banner')}
      >
        {/*
          Direct <img> tag explainer:

          BpkImage is not used in this case as this specific usecase is slightly different than many images.
          The design spec of this component requires that the OurCookiePromise image should bleed outside of it's parent to create an overlaping look.
          In addition, BpkImage has complications when using it with .svg source and it is a parent of a flex container (this specific scenario).
          With these complications/designs we have chosen to not use BpkImage.
          BpkImage should remain the default option for images unless specific usecases do not work.

          */}
        <img
          className={STYLES['cookie-banner__image']}
          alt=""
          width="80"
          height="100"
          src="https://content.skyscnr.com/m/67dcbbe580384179/original/Cookie-Promise.svg"
        />
        <div
          tabIndex={-1}
          ref={cookieHeaderRef}
          className={STYLES['cookie-banner__message']}
          data-testid="cookieBannerMessage"
        >
          <BpkText tagName="h2" textStyle={TEXT_STYLES.heading3}>
            {strings.title}
          </BpkText>
          <BpkText tagName="p" textStyle={TEXT_STYLES.sm}>
            {reactReplacePlaceholder(
              strings.declineAllSectionA,
              contextCookiePolicyLink(logger, cookiePolicyUrl, trackEventFn),
            )}
          </BpkText>
          <BpkText
            tagName="p"
            textStyle={TEXT_STYLES.sm}
            className={STYLES['cookie-banner__explain-options']}
          >
            {reactReplacePlaceholder(
              strings.declineAllSectionB?.replace(
                '@@totalNumberOfVendors@@',
                totalNumberOfVendors || '',
              ),
              contextCookiePolicyLink(logger, cookiePolicyUrl, trackEventFn),
            )}
          </BpkText>
          <BpkText
            tagName="p"
            textStyle={TEXT_STYLES.sm}
            className={STYLES['cookie-banner__explain-options']}
          >
            {reactReplacePlaceholder(
              strings.declineAllSectionC,
              contextCookiePolicyLink(logger, cookiePolicyUrl, trackEventFn),
            )}
          </BpkText>
        </div>
        <div className={STYLES['cookie-banner__buttons']}>
          <BpkButton
            className={[
              STYLES['cookie-banner__button'],
              STYLES['cookie-banner__button-accept'],
            ].join(' ')}
            onClick={(clickEvent: React.MouseEvent<HTMLButtonElement>) => {
              // unsubscribing so that we don't fire the onPolicyAcceptClick twice
              observer.unsubscribe(trackingPreferencesChangedSubscription);

              prepareAndSaveGDPRPreferences({ accepted: true });

              trackEventFn(cookieBannerTrackingEvent('Accepted'));

              prefChangeNewRelicAndExpTrigger();

              // This event is used by other consumers to react to cookies being accepted
              // e.g. Google yolo login banner, Google ads request
              observer.publish('cookie-policy-accepted');

              onPolicyAcceptClick(clickEvent);
            }}
            // Although this component does not directly depend upon the Cookie Banner button *id*, many squads - in their E2E tests - use the id
            // to unambiguously find the OK button, allowing them to easily accept the cookie banner at the beginning of their tests.
            // We took this out originally as an anti-pattern to not depend on ids, but the dependency in the E2E tests means it is
            // simplest to just reinstate the id as it was originally, which unblocks those depending on it.
            data-tracking-element-id="cookie_banner_accept_all"
            data-tracking-common-params={mbdTrackingParams('cookie_banner_accept_all')}
            id="acceptCookieButton"
          >
            {strings.declineAllButtonAcceptAll}
          </BpkButton>
          <BpkButton
            type={BUTTON_TYPES.secondary}
            className={[
              STYLES['cookie-banner__button'],
              STYLES['cookie-banner__button-manage'],
            ].join(' ')}
            // this URL isn't localised by UMS, which is why it's hard-coded here
            href="/privacy-settings"
            onClick={() => {
              trackEventFn(cookieBannerTrackingEvent('Manage Preferences Clicked'));
              logger.logOperationalEvent({
                eventName: 'Manage Preferences Clicked',
                component: componentName,
              });
            }}
            data-tracking-element-id="cookie_banner_manage_settings"
            data-tracking-common-params={mbdTrackingParams('cookie_banner_manage_settings')}
          >
            {strings.declineAllButtonManageSettings}
          </BpkButton>
          <BpkButton
            type={BUTTON_TYPES.secondary}
            className={[
              STYLES['cookie-banner__button'],
              STYLES['cookie-banner__button-accept-essential-only'],
            ].join(' ')}
            onClick={() => {
              prepareAndSaveGDPRPreferences({ accepted: false });

              trackEventFn(cookieBannerTrackingEvent('Declined'));
              logger.logOperationalEvent({ eventName: 'Declined', component: componentName });
            }}
            data-tracking-element-id="cookie_banner_essential_only"
            data-tracking-common-params={mbdTrackingParams('cookie_banner_essential_only')}
          >
            {strings.declineAllButtonAcceptEssentialOnly}
          </BpkButton>
        </div>
      </div>
    </div>
  );
};
/**
 * These are the props required in order to render the CookieBanner correctly.
 */
export interface CookieBannerProps {
  /**
   * Indicates whether the environment is production or not. Used to determine if the `StringsPropVerification` wrapper should render error states in the UI.
   */
  isProductionEnv: boolean;
  /**
   * URL linking to the detailed cookie policy.
   * @defaultValue `/cookiepolicy.aspx`
   */
  cookiePolicyUrl?: string;
  /**
   * The specific market of the user.
   */
  market: string;
  /**
   *  A function called when the user accepts the cookie policy.
   */
  onPolicyAcceptClick: (event: MouseEvent<HTMLButtonElement> | null) => void;
  /**
   * Object containing translated strings for the component.
   */
  strings: Strings;
  /**
   * Object for logging operational and warning events related to the component.
   */
  logger: Logger;
  /**
   * Function to track user interaction events within the component. If not provided the `defaultTrackEventFn` will be used.
   */
  trackEventFn?: TrackingEventFn;
  /**
   * This value needs to be passed to have the control version of TCF config that is used.
   */
  transparencyConsentFrameworkConfigVersionNumber?: string;
  /**
   * Indicates the number of Vendors that is passed down from the cosumer and is displayed in the vendor text.
   */
  totalNumberOfVendors?: string;
  /**
   * The current active Skyscanner vendor list from the config service. This will be given as a string containing an array and parsed within the component.
   */
  activeVendorList?: string;
  /**
   * Boolean flag to disable the scrim that appears behind the cookie banner.
   * When `true`, the banner renders inline without the full-screen scrim/portal,
   * allowing the user to interact with the page beneath it (e.g. to read and
   * scroll the Cookie Policy before making a consent decision).
   * @defaultValue `false`
   */
  disableScrim?: boolean;
}

/**
 * @privateRemarks
 * This extra layer of wrapping is required as if we detect an issue with strings we'd like both the cookie banner content _and_
 * the scrim to not be rendered in a prod env.
 * Without this the StringsErrorBoundary sits below the scrim(created via withScrim) in the render tree and thus would be rendered
 * in production, this is not desired.
 * This pattern also allows us to enforce a getApplicationElement implementation which all microsites should adhere to.
 */
// @ts-expect-error — bpk-scrim-utils withScrimmedPortal type mismatch
const WrappedCookieBannerContent = withScrimmedPortal(CookieBannerContent);

/**
 * `CookieBanner` Component.
 *
 * The `CookieBanner` component displays a GDPR compliant banner to inform users about the usage of cookies
 * and allow them to accept or manage their cookie preferences. This banner is essential for compliance with
 * European privacy regulations.
 *
 * @example
 * ```
 * const enStrings = {
 *   title: "We value your privacy",
 *   declineAllSectionA: "To personalise your experience we collect information on how you use Skyscanner, which includes personal data such as your IP address. This is to provide an improved experience, store and/or access information on a device, personalised advertising, advertising measurement, audience research and services development. Read our @@tag1@@Cookie Policy@@tag2@@.",
 *   declineAllSectionB: "We and @@tag3@@our @@totalNumberOfVendors@@ partners@@tag4@@ may rely on legitimate interest for certain purposes rather than consent. Accept all cookies by choosing 'Accept all' or only allow essential cookies by selecting 'Accept essential only'. Go to 'Manage settings' to update preferences.",
 *   declineAllSectionC: "Manage preferences or withdraw consent at any time on @@tag5@@Privacy Settings@@tag6@@.",
 *   declineAllButtonAcceptAll: "Accept all",
 *   declineAllButtonManageSettings: "Manage settings",
 *   declineAllButtonAcceptEssentialOnly: "Accept essential only",
 *
 *   // The properties below are required due to legacy validation but are unused and will be deprecated in the future.
 *   consentInformationSectionA: "Some of our {totalNumberOfVendors} vendors may store and process your personal data on the basis of legitimate interest or consent, which you can object to by choosing 'Cookie Preferences' below. Look for 'Privacy Settings' link in the footer of this page where you can withdraw consent.",
 *   consentInformationSectionB: "To offer you a more personalised experience, we (and the @@tag3@@third party vendors@@tag4@@ we work with) collect info on how and when you use Skyscanner. It helps us remember your details, show relevant ads and improve our services. More info is in our  @@tag1@@Cookie Policy@@tag2@@.",
 *   consentInformationSectionD: "Manage your preferences or withdraw consent at any time on the Privacy Settings page in the footer.",
 *   explainYourOptions: "Please select 'OK' to allow all cookies. Or choose 'Cookie preferences' to customise your settings and opt out of all or some non-essential cookies.",
 *   close: "OK",
 *   manageOptions: "Cookie preferences"
 * };
 *
 *   // Example with required props only
 *   <CookieBanner
 *     isProductionEnv
 *     market="UK"
 *     onPolicyAcceptClick={handlePolicyAccept}
 *     strings={enStrings}
 *     logger={logger}
 *   />
 *
 *   // Example including optional props, refer to CookieBannerProps interface for details
 *   <CookieBanner
 *     isProductionEnv
 *     market="UK"
 *     onPolicyAcceptClick={handlePolicyAccept}
 *     strings={enStrings}
 *     logger={logger}
 *     // Optional props below
 *     cookiePolicyUrl="/cookiepolicy.aspx"
 *     trackEventFn={handleTrackEvent}
 *     transparencyConsentFrameworkConfigVersionNumber="107/b615c0b1a34c2ea66234e79db77c199649ad4c49bbf76f8da1652eb194aac155"
 *     totalNumberOfVendors="1000"
 *     activeVendorList="[1,2,3,4,5]"
 *   />
 * ```
 *
 * @remarks
 * The component uses the `saddlebag-tracking-preferences` library to handle the GDPR preferences of the user.
 * `CookieBannerContent` is the core component displaying the actual content of the banner.
 * `withScrim` Higher Order Component (HOC) is used to wrap the `CookieBannerContent` with a scrim for UI purposes.
 * `StringsPropVerification` wrapper ensures the integrity of localized strings passed to the component, especially in production environments.
 * Important: The ID `acceptCookieButton` on the accept button is kept for compatibility with E2E tests of many squads.
 *
 */

const CookieBanner = (props: CookieBannerProps) => {
  // On the scrim-disabled path we render `CookieBannerContent` directly, so we must
  // supply the `dialogRef` ourselves (on the default path the `withScrimmedPortal` HOC
  // injects it).
  const dialogRef = useRef<HTMLDivElement>(null);

  return (
    <StringsPropVerification
      componentName={CookieBanner.name}
      isProductionEnv={props.isProductionEnv || false}
      logger={props.logger}
      strings={props.strings}
      stringsKeys={stringsKeys}
    >
      {props.disableScrim ? (
        // No scrim/portal: render inline so the user can interact with (read/scroll)
        // the page beneath the banner — e.g. the Cookie Policy page.
        <CookieBannerContent {...props} dialogRef={dialogRef} />
      ) : (
        <WrappedCookieBannerContent
          {...props}
          closeOnScrimClick={false}
          getApplicationElement={() => document.querySelector('#pagewrap')}
          renderTarget={() => document.getElementById('modal-container')}
          // The containerClassName is very important and exists for a reason. Some browsers (e,g. Brave) or ad-blockers will remove GDPR cookie banners. Without this wrapper telling
          // these services that this is a cookie banner, only part of the cookie banner may be removed, leaving behind the scrim. This renders the page unusable.
          containerClassName="cookie-banner-wrapper"
        />
      )}
    </StringsPropVerification>
  );
};

export default CookieBanner;
